import { useT } from "@/i18n";
import { Link } from "@tanstack/react-router";
import {
  HiOutlineAcademicCap,
  HiOutlineBookOpen,
  HiOutlineChartSquareBar,
  HiOutlineClipboardCheck,
  HiOutlineClipboardList,
  HiX,
} from "react-icons/hi";

const ITEMS = [
  { key: "books", label: "nav.books", to: "/teacher/books", Icon: HiOutlineBookOpen },
  {
    key: "classrooms",
    label: "nav.classrooms",
    to: "/teacher/classrooms",
    Icon: HiOutlineAcademicCap,
  },
  {
    key: "assign",
    label: "nav.assignHomework",
    to: "/teacher/assign",
    Icon: HiOutlineClipboardList,
  },
  { key: "check", label: "nav.checkHomework", to: "/teacher/check", Icon: HiOutlineClipboardCheck },
  {
    key: "selfStudy",
    label: "nav.selfStudyRecord",
    to: "/teacher/self-study-record",
    Icon: HiOutlineChartSquareBar,
  },
] as const;

const ITEM_CLASS =
  "flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-left font-kumbh text-sm font-bold text-app-ink transition-colors hover:bg-app-sky-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-app-ink";

/**
 * The teacher area's section panel: beside the page on wide screens, a drawer behind the navbar's
 * burger on narrow ones. `onClose` runs on every link too, so the drawer never outlives a jump.
 */
export function TeacherSidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const t = useT();

  const items = ITEMS.map(({ key, label, to, Icon }) => (
    <Link key={key} to={to} onClick={onClose} className={ITEM_CLASS}>
      <Icon className="h-5 w-5 shrink-0 text-app-ink" aria-hidden="true" />
      {t[label]}
    </Link>
  ));

  return (
    <>
      <nav
        aria-label={t["nav.sidebar"]}
        className="app-card sticky top-20 hidden w-56 shrink-0 flex-col gap-1 self-start p-2 lg:flex"
      >
        {items}
      </nav>

      {isOpen && (
        <div className="lg:hidden">
          <button
            type="button"
            aria-label={t["nav.closeMenu"]}
            onClick={onClose}
            className="fixed inset-0 z-30 cursor-default bg-black/30 backdrop-blur-sm"
          />

          <nav
            aria-label={t["nav.sidebar"]}
            className="fixed top-0 left-0 z-40 flex h-full w-64 flex-col gap-1 bg-white p-3 shadow-app-card"
          >
            <button
              type="button"
              aria-label={t["nav.closeMenu"]}
              onClick={onClose}
              className="mb-1 self-end rounded-full p-2 text-app-ink hover:bg-app-sky-soft"
            >
              <HiX className="h-5 w-5" aria-hidden="true" />
            </button>
            {items}
          </nav>
        </div>
      )}
    </>
  );
}
